import { DEFAULT_LANGUAGE } from '../constants/languages';
import type {
    SupportedLanguage,
    TranslationKey,
    TranslationParams,
} from '../types/i18nTypes';

/**
 * Get nested translation value by key
 */
function getNestedValue(obj: TranslationKey, path: string): unknown {
    return path.split('.').reduce<unknown>((current, key): unknown => {
        if (current && typeof current === 'object' && key in current) {
            return (current as Record<string, unknown>)[key];
        }
        return undefined;
    }, obj);
}

/**
 * Interpolate variables in a translation string
 * Example: "Hello {{name}}" with { name: "World" } -> "Hello World"
 */
export function interpolateTranslation(
    translation: string,
    params: TranslationParams = {},
): string {
    if (!params || Object.keys(params).length === 0) {
        return translation;
    }

    return Object.entries(params).reduce((result, [key, value]): string => {
        const placeholder = `{{${key}}}`;
        return result.replace(new RegExp(placeholder, 'g'), String(value));
    }, translation);
}

/**
 * Get translation by key with support for:
 * - Nested keys (e.g.: "header.menu.logout")
 * - Fallback to default language
 * - Fallback to key itself
 * - Variable interpolation
 */
export function getTranslation(
    key: string,
    language: SupportedLanguage,
    translations: Record<SupportedLanguage, TranslationKey>,
    fallbackLanguage: SupportedLanguage = DEFAULT_LANGUAGE,
    params: TranslationParams = {},
): string {
    // Try to get in the selected language
    let value = getNestedValue(translations[language], key);

    // Fallback to default language
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (value == null) {
        value = getNestedValue(translations[fallbackLanguage], key);
    }

    // Fallback to key itself (for development)
    if (value === undefined || value === null) {
        return key;
    }

    // Ensure it is a string
    if (typeof value !== 'string') {
        return key;
    }

    // Interpolate variables
    return interpolateTranslation(value, params);
}
